import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
  CircularProgress,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm, Controller } from 'react-hook-form';
import {
  ProblemTag,
  createTag,
  updateTag,
  deleteTag
} from '../../../../services/problemPoolService';

interface TagManagerProps {
  tags: ProblemTag[];
  onTagsChange: () => void;
}

interface TagFormData {
  name: string;
  color: string;
}

const defaultColors = [
  '#10B981', // Green
  '#3B82F6', // Blue
  '#8B5CF6', // Purple
  '#F59E0B', // Yellow
  '#EF4444', // Red
  '#6366F1', // Indigo
  '#EC4899', // Pink
  '#14B8A6', // Teal
  '#6B7280', // Gray
  '#F97316'  // Orange
];

const TagManager: React.FC<TagManagerProps> = ({
  tags,
  onTagsChange
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<ProblemTag | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<TagFormData>({
    defaultValues: {
      name: '',
      color: defaultColors[0]
    }
  });

  const handleOpenCreate = () => {
    setSelectedTag(null);
    reset({
      name: '',
      color: defaultColors[tags.length % defaultColors.length]
    });
    setError(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (tag: ProblemTag) => {
    setSelectedTag(tag);
    reset({
      name: tag.name,
      color: tag.color
    });
    setError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTag(null);
  };

  const handleOpenDelete = (tag: ProblemTag) => {
    setSelectedTag(tag);
    setDeleteDialogOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteDialogOpen(false);
    setSelectedTag(null);
  };

  const onSubmit = async (data: TagFormData) => {
    setLoading(true);
    setError(null);

    try {
      if (selectedTag) {
        await updateTag(selectedTag.id, data);
      } else {
        await createTag(data);
      }
      handleCloseDialog();
      onTagsChange();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setError(error.response?.data?.message || error.message || 'Failed to save tag');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedTag) return;

    setLoading(true);
    try {
      await deleteTag(selectedTag.id);
      handleCloseDelete();
      onTagsChange();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setError(error.response?.data?.message || error.message || 'Failed to delete tag');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Problem Tags</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          New Tag
        </Button>
      </Box>

      {error && !dialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tag</TableCell>
              <TableCell align="center">Problems</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tags.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography color="text.secondary">No tags yet</Typography>
                </TableCell>
              </TableRow>
            ) : (
              tags.map(tag => (
                <TableRow key={tag.id}>
                  <TableCell>
                    <Chip
                      label={tag.name}
                      sx={{
                        bgcolor: tag.color,
                        color: 'white'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">{tag.problemCount || 0}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenEdit(tag)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenDelete(tag)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {selectedTag ? 'Edit Tag' : 'Create Tag'}
          </DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Name is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Name"
                    fullWidth
                    required
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    inputProps={{ maxLength: 50 }}
                  />
                )}
              />

              <Controller
                name="color"
                control={control}
                render={({ field }) => (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Color
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {defaultColors.map(color => (
                        <Box
                          key={color}
                          onClick={() => field.onChange(color)}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: color,
                            cursor: 'pointer',
                            border: field.value === color ? '3px solid #000' : 'none',
                            '&:hover': { opacity: 0.8 }
                          }}
                        />
                      ))}
                    </Box>
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      size="small"
                      sx={{ mt: 1, width: 120 }}
                      inputProps={{ maxLength: 7 }}
                    />
                  </Box>
                )}
              />

              {/* Preview */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Preview
                </Typography>
                <Controller
                  name="name"
                  control={control}
                  render={({ field: nameField }) => (
                    <Controller
                      name="color"
                      control={control}
                      render={({ field: colorField }) => (
                        <Chip
                          label={nameField.value || 'Tag Name'}
                          sx={{
                            bgcolor: colorField.value,
                            color: 'white'
                          }}
                        />
                      )}
                    />
                  )}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {selectedTag ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDelete}>
        <DialogTitle>Delete Tag</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the tag "{selectedTag?.name}"?
            {selectedTag?.problemCount ? (
              <Box component="span" sx={{ display: 'block', mt: 1 }}>
                This tag is used by {selectedTag.problemCount} problem(s). It will be removed from them.
              </Box>
            ) : null}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TagManager;
